import { helpers, utils, query, models, stores } from '@kurtosys/ksys-app-template';
import {
	IComponentStyle,
	CacheOptions,
	IApplicationAssetRegister,
	ISkeletonLoaders,
	ISkeletonLoader,
	IQueryContext,
	IPixelDimensions,
} from '../../../models/commonTypes';
import { getApplicationCode } from '../../../start';
import { StoreBase } from '../../../common/StoreBase';
import { CONFIGURATION } from '../../../configuration/development.config';
import { AUTHENTICATION } from '../../../configuration/development.authentication';
import { APPLICATION_CLIENT_CONFIGURATION_IDS } from '../../../configuration/development.applicationClientConfigurationIds';
import { APPLICATION_CLIENT_CONFIGURATIONS } from '../../../configuration/development.applicationClientConfigurations';
import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration';
import { STYLES } from '../../../configuration/development.styles';
import { IConfiguration } from '../../../models/app/IConfiguration';
import { IInputs } from '../../../models/app/IInputs';
import { IStyles } from '../../../models/app/IStyles';
import { IAppComponents } from '../models/IAppComponents';
import { IComponentConfigurations } from '../../../models/app/IComponentConfigurations';
import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IDebugInfo } from '../../../models/app/IDebugInfo';
import { observable, computed, action } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { IGetApplicationAppConfigResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/config/IGetApplicationAppConfigResponseBody';
import { registerAppStart, registerAppLoaded, getAppHydration, getAppKey } from '../../../utils/initialize';
import { IAppConfiguration } from '../models/IAppConfiguration';
import { ICoreConfigurations } from '../../../models/app/ICoreConfigurations';
import { IAssets } from '@kurtosys/ksys-app-components/dist/models/IAssets';
import { Manifest } from '../../../configuration/Manifest';
import { getPreviewContext } from '../../../initialize';
import { ITheme } from '@kurtosys/ksys-app-components/dist/models/ITheme';
import { IGetThemeResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/applicationManager/IGetThemeResponseBody';
import { getStoreKey } from '../../../utils/store';
import { IApplicationEmbedProps } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/models/IApplicationEmbedProps';
import { IApplicationEmbedInput } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/models/IApplicationEmbedInput';
import { ICardProps } from '@kurtosys/ksys-app-components/dist/components/base/Card/models';
import { IDisclaimerProps } from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer/models/IDisclaimerProps';
import { getGlobalDisclaimerStore } from '../../../utils/globalDisclaimers';

export const DATA_CONTEXT_SEED_KEY = '__ksys-app-data-context-seed__';
export abstract class AppStoreBase extends StoreBase {
	abstract get hasData(): boolean;
	htmlElement: HTMLElement;
	storeKey: string;
	appParamsHelper: helpers.AppParamsHelper<IInputs, IConfiguration, IStyles>;
	@observable.ref rawConfiguration: Partial<IConfiguration> = {};
	@observable.ref rawStyles: Partial<IStyles> = {};
	@observable.ref rawThemeResponse: IGetThemeResponseBody | undefined;
	@observable.ref authentication: models.appsInitialize.IAppAuthentication | undefined;
	@observable.ref applicationClientConfigurationIds: number[] | undefined;
	@observable.ref applicationClientConfigurations: models.api.applicationManager.IApplicationClientConfiguration[] = [];
	@observable themeKey: string | undefined;
	// App loading states
	@observable isBootstrapped = false;

	constructor(element: HTMLElement, url: string, storeContext: StoreContext, public manifest: Manifest) {
		super(storeContext);
		this.htmlElement = element;
		this.storeKey = getStoreKey(element, url, manifest);
		this.appParamsHelper = new helpers.AppParamsHelper(element, url);
		registerAppStart(manifest, this.appParamsHelper);
		this.themeKey = (this.appParamsHelper.rawAppParams && this.appParamsHelper.rawAppParams.themeKey) || 'default';
	}

	getTranslateFunction = () => {
		const { translationStore } = this.storeContext;
		return translationStore.translate;
	}

	getAccessibilityTextFunction = () => {
		const { accessibilityStore } = this.storeContext;
		return accessibilityStore.accessibilityText;
	}

	@computed
	get culture(): string {
		const { translationStore } = this.storeContext;
		return translationStore && translationStore.culture || 'en-GB';
	}

	// Left blank to allow override
	async customInitializeAfter() {

	}

	// Left blank to allow override
	async customInitializeBefore() {

	}

	@computed
	get isDevelopment(): boolean {
		return this.manifest.isDevelopment;
	}

	@computed
	get applicationGuid(): string | undefined {
		return this.manifest.getApplicationGuid(this.appParamsHelper);
	}

	@computed
	get isDebug(): boolean {
		const value = utils.url.getQueryStringValue({ key: 'ksysAppDebug' }, window.location.search);
		return value === 'true';
	}

	logDebugInfo = (): void => {
		const debugInfo = this.getDebugInfo();
		const debugInfoString = JSON.stringify(debugInfo);
		console.log(debugInfoString);
		console.log('Debug Info copied to clipboard');
		utils.clipboard.copyToClipboard(debugInfoString);
	}

	getDebugInfo = (): IDebugInfo => {
		return {
			manifest: this.manifest.serialize(),
			applicationCode: getApplicationCode(),
			applicationClientConfigurationIds: this.applicationClientConfigurationIds,
			applicationClientConfigurations: this.applicationClientConfigurations,
			authentication: this.authentication,
			configuration: this.rawConfiguration,
			style: this.rawStyles,
			theme: this.rawThemeResponse,
		};
	}

	@action
	async initialize() {
		const response = await this.getAppConfig();
		if (response) {
			const { kurtosysApiStore } = this.storeContext;
			this.authentication = response.authentication;
			const token = (this.authentication && this.authentication.token) || '';
			kurtosysApiStore.token = token;
			this.applicationClientConfigurationIds = response.applicationClientConfigurationIds;
			this.rawStyles = response.applicationStyles as any as IStyles;
			this.rawConfiguration = await this.buildConfiguration(response.applicationConfiguration as any as IConfiguration);
			const stopInitializeConfiguration = await this.loadPreviewContext();
			if (!stopInitializeConfiguration) {
				await this.initializeConfiguration();
			}
		}
	}

	@action
	async reInitialize() {
		const stopInitializeConfiguration = await this.loadPreviewContext();
		if (!stopInitializeConfiguration) {
			await this.initializeConfiguration();
		}
	}

	async buildConfiguration(configuration: IConfiguration): Promise<IConfiguration> {
		let response: IConfiguration = {};
		const applicationClientConfigurationIds = this.applicationClientConfigurationIds || [];
		if (applicationClientConfigurationIds && applicationClientConfigurationIds.length > 0) {
			if (this.isDevelopment) {
				if (this.applicationClientConfigurations.length === 0) {
					this.applicationClientConfigurations = [...APPLICATION_CLIENT_CONFIGURATIONS];
				}
				const results = applicationClientConfigurationIds.map((id) => {
					const applicationClientConfiguration = this.applicationClientConfigurations.find(a => a.applicationClientConfigurationId === id);
					return applicationClientConfiguration;
				});
				const errors = results.filter(result => !result);
				if (errors.length === 0) {
					response = utils.object.deepMergeObjects(response, ...results.map(result => (result && result.configuration) || {}));
				}
				else {
					console.warn(`applicationClientConfigurationIds don't have all available application client configurations in the APPLICATION_CLIENT_CONFIGURATIONS collection`, { APPLICATION_CLIENT_CONFIGURATIONS, APPLICATION_CLIENT_CONFIGURATION_IDS });
				}
			}
			else {
				if (this.authentication && this.authentication.token) {
					const promises: Promise<models.api.applicationManager.IApplicationClientConfiguration>[] = [];
					for (const applicationClientConfigurationId of applicationClientConfigurationIds) {
						const existingApplicationClientConfiguration = this.applicationClientConfigurations.find(a => a.applicationClientConfigurationId === applicationClientConfigurationId);
						if (existingApplicationClientConfiguration) {
							promises.push(new Promise((resolve) => {
								resolve(existingApplicationClientConfiguration);
							}));
						}
						const { kurtosysApiStore } = this.storeContext;
						const queryString: any = {
							applicationClientConfigurationId,
						};
						promises.push(
							kurtosysApiStore.getApplicationClientConfiguration.execute({
								queryString,
							}),
						);
					}
					const results = await Promise.all<models.api.applicationManager.IApplicationClientConfiguration>(promises.map(p => p.catch(e => e)));
					const errors = results.filter(result => (result instanceof Error));
					if (errors.length === 0) {
						const resultsToAdd = results.filter((result) => {
							return !this.applicationClientConfigurations.some(a => a.applicationClientConfigurationId === result.applicationClientConfigurationId);
						});
						if (resultsToAdd && resultsToAdd.length > 0) {
							this.applicationClientConfigurations = [
								...this.applicationClientConfigurations,
								...resultsToAdd,
							];
						}
						response = utils.object.deepMergeObjects(response, ...results.map(result => result.configuration));
					}
					else {
						console.warn({ buildConfigurationErrors: errors });
					}
				}
			}
		}
		if (configuration) {
			response = utils.object.deepMergeObjects(response, configuration);
		}
		return response;
	}

	@action
	async loadPreviewContext(): Promise<boolean> {
		let response = false;
		const applicationCode = getApplicationCode();
		const previewContext = getPreviewContext(applicationCode);
		if (previewContext) {
			if (previewContext.themeKey && this.themeKey !== previewContext.themeKey) {
				this.themeKey = previewContext.themeKey;
				await this.loadAppTheme();
			}
			if (previewContext.theme && this.rawThemeResponse) {
				this.rawThemeResponse = {
					...this.rawThemeResponse,
					theme: previewContext.theme,
				};
				this.rawStyles = previewContext.applicationStyles || { ...this.rawStyles };

			}
			if (previewContext.applicationStyles) {
				this.rawStyles = previewContext.applicationStyles;
			}
			if (previewContext.applicationConfiguration) {
				this.rawConfiguration = previewContext.applicationConfiguration;
				this.initializeConfiguration(true);
				response = true;
			}
		}
		return response;
	}

	@action
	initializeConfiguration = async (preventAppLoadedEventFire: boolean = false) => {
		const { kurtosysApiStore, translationStore, queryStore } = this.storeContext;
		const token = (this.authentication && this.authentication.token) || (this.configuration && this.configuration.token) || '';
		kurtosysApiStore.token = token;
		// Load up the inputs from queries defined in the configuration
		this.initializeInputsFromConfiguration();
		this.loadVersionRegistry();
		await this.customInitializeBefore();
		const promises: Promise<any>[] = [
			this.loadAppTheme(),
			translationStore.loadTranslations(),
			queryStore.initialize(),
		];
		await Promise.all(promises.map(p => p.catch(e => e)));
		await this.customInitializeAfter();
		this.isBootstrapped = true;
		if (!preventAppLoadedEventFire) {
			const { loadEventDelay = 0 } = (this.rawConfiguration && this.rawConfiguration.core) || {};
			setTimeout(() => {
				registerAppLoaded(this.manifest, this.appParamsHelper);
			}, loadEventDelay);
		}
	}

	@action loadVersionRegistry = () => {
		const key = '__ksysAppVersionRegistry__';
		if (!(window as any)[key]) {
			(window as any)[key] = [];
		}
		const registry = (window as any)[key];
		const applicationCode = getApplicationCode();
		const templateId = this.manifest.ksysAppTemplateId;
		const version = this.manifest.version;
		registry.push({
			applicationCode,
			templateId,
			version,
		});
	}

	@action
	initializeInputsFromConfiguration = () => {
		const { inputs } = this.coreConfigurations;
		if (inputs) {
			const { key, queries, sourceKeys } = inputs;
			const globalInputs = this.globalInputs;
			if (sourceKeys && sourceKeys.length > 0) {
				sourceKeys.forEach((sourceKey) => {
					const sourceInputs = globalInputs[sourceKey];
					if (sourceInputs) {
						this.appParamsHelper.loadInputsFromConfiguration(sourceInputs);
					}
				});
			}
			if (queries) {
				const values: { [key: string]: any } = {};
				queries.forEach((queryOptions) => {
					const { queryId = 'NO QUERY ID' } = queryOptions;
					const executionOptions = {
						inputs: this.appParamsHelper.inputs,
					};
					const { queryStore } = this.storeContext;
					const value = queryStore.query(queryOptions, executionOptions);
					this.appParamsHelper.loadInputsFromConfiguration({ [queryId]: value });
					values[queryId] = value;
				});
				if (key) {
					this.globalInputs[key] = values;
				}
			}
		}
	}

	inputsWindowKey = '__ksysDynamicInputs__';
	get globalInputs(): { [key: string]: object } {
		if (!(window as any)[this.inputsWindowKey]) {
			(window as any)[this.inputsWindowKey] = {};
		}
		return (window as any)[this.inputsWindowKey];
	}

	set globalInputs(value: { [key: string]: object }) {
		(window as any)[this.inputsWindowKey] = value;
	}

	@computed
	get skeletonLoaders(): ISkeletonLoaders | undefined {
		return this.coreConfigurations && this.coreConfigurations.skeletonLoaders;
	}

	@computed
	get skeletonLoader(): ISkeletonLoader | undefined {
		const skeletonLoaders = this.skeletonLoaders;
		if (skeletonLoaders) {
			const { loaders = [] } = skeletonLoaders;
			return loaders.find((loader) => {
				const { id, configurationKey: loaderConfigurationKey = 'default', styleKey: loaderStyleKey = 'default' } = loader;
				const { configurationKey = 'default', styleKey = 'default' } = this.appParamsHelper.rawAppParams || {};
				return id === this.manifest.ksysAppTemplateId && configurationKey === loaderConfigurationKey && styleKey === loaderStyleKey;
			});
		}
	}

	@computed
	get rawTheme(): ITheme {
		return (this.rawThemeResponse && this.rawThemeResponse.theme) || {};
	}

	@computed
	get show(): boolean {
		if (this.appComponentConfiguration && this.appComponentConfiguration.noDataOptions) {
			const { hide = true } = this.appComponentConfiguration.noDataOptions;
			if (!hide) {
				return true;
			}
		}
		return this.customHasDataValidation(this.hasData);
	}

	@computed
	get showPlaceholder(): boolean {
		return (this.show && !this.customHasDataValidation(this.hasData) && (this.noDataPlaceholder !== undefined || this.noDataPlaceholderDisclaimer !== undefined));
	}

	@computed
	get serviceUrl(): string | undefined {
		if (this.storeContext) {
			const { kurtosysApiStore } = this.storeContext;
			if (kurtosysApiStore) {
				return kurtosysApiStore.serviceUrl;
			}
		}
		return;
	}

	customHasDataValidation(hasData: boolean): boolean {
		if (this.appComponentConfiguration) {
			const { noDataOptions } = this.appComponentConfiguration;
			if (noDataOptions) {
				const { customValidation } = noDataOptions;
				if (customValidation) {
					const { queryStore } = this.storeContext;
					const { includeDefaultValidation, validateWithDefaultAs, conditionalValidation } = customValidation;
					const conditionalHelper = new helpers.ConditionalHelper(conditionalValidation);
					const isValid = conditionalHelper.matchesWithOptions({ executionOptions: queryStore.executionOptions });
					if (includeDefaultValidation) {
						switch (validateWithDefaultAs) {
							case 'ANY':
								hasData = hasData || isValid;
								break;
							case 'BOTH':
							default:
								hasData = hasData && isValid;
								break;
						}
					}
					else {

						hasData = isValid;
					}
				}
			}
		}
		return hasData;
	}

	@computed
	get noDataPlaceholder(): string | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.noDataOptions && this.appComponentConfiguration.noDataOptions.noDataPlaceholder;
	}

	@computed
	get noDataPlaceholderDisclaimer(): IDisclaimerProps | undefined {
		const placeHolderText = this.appComponentConfiguration && this.appComponentConfiguration.noDataOptions && this.appComponentConfiguration.noDataOptions.noDataPlaceholderDisclaimer;
		return this.mergeQueriesAndProps(placeHolderText);
	}

	@computed
	get dataContextSeed(): IQueryContext | undefined {
		const contextSeedConfiguration = this.coreConfigurations && this.coreConfigurations.contextSeed;
		if (contextSeedConfiguration) {
			const contextAndConfiguration = query.Query.contextStore.get(contextSeedConfiguration);
			if (contextAndConfiguration && contextAndConfiguration.context) {
				return contextAndConfiguration.context;
			}
		}
		const applicationCode = getApplicationCode();
		const keyWithApplicationCode = `${ DATA_CONTEXT_SEED_KEY }${ applicationCode || '' }`;
		if ((window as any)[keyWithApplicationCode]) {
			return (window as any)[keyWithApplicationCode];
		}
		return undefined;
	}

	@action
	async getAppConfig(): Promise<IGetApplicationAppConfigResponseBody> {
		const appFromHydration = getAppHydration(this.manifest, this.appParamsHelper);
		if (appFromHydration) {
			const { applicationConfiguration, applicationStyles, authentication, applicationClientConfigurationIds } = appFromHydration;
			return {
				applicationConfiguration,
				applicationStyles,
				authentication,
				applicationClientConfigurationIds,
			};
		}
		// We want to use the hard coded configuration when developing locally
		// otherwise fetch it
		let response: IGetApplicationAppConfigResponseBody = {
			applicationConfiguration: CONFIGURATION,
			applicationStyles: STYLES,
			authentication: AUTHENTICATION,
			applicationClientConfigurationIds: APPLICATION_CLIENT_CONFIGURATION_IDS,
		};
		if (!this.isDevelopment) {
			response = await this.getAppConfigFromApi();
		}
		return response;
	}

	@action
	async getAppConfigFromApi(): Promise<IGetApplicationAppConfigResponseBody> {
		const { rawAppParams = {} } = this.appParamsHelper;
		const { configurationKey = 'default', styleKey } = rawAppParams;
		const urlParams: string[] = [
			configurationKey,
		];
		// Only add the styleKey if it is defined and not default to help with backwards compatibility, until services are deployed.
		if (styleKey && styleKey !== 'default') {
			urlParams.push(styleKey);
		}
		const { kurtosysApiStore } = this.storeContext;
		return await kurtosysApiStore.getAppConfig.execute({
			urlParams,
		});
	}

	@action
	async loadAppTheme(): Promise<void> {
		const themeKey = this.themeKey;
		if (!this.rawThemeResponse || this.rawThemeResponse.name !== themeKey) {
			const { kurtosysApiStore } = this.storeContext;
			try {
				const queryString: any = {
					type: 'apps',
					name: themeKey,
				};
				const themeResponse = await kurtosysApiStore.getTheme.execute({
					queryString,
				});
				if (themeResponse) {
					this.rawThemeResponse = themeResponse;
				}
			}
			catch (ex) {
				console.warn(ex);
			}
		}
	}

	@computed
	get assetsBaseUrl(): string {
		const { kurtosysApiStore } = this.storeContext;
		return kurtosysApiStore.getBaseAddress('');
	}

	@computed
	get assetCacheOptions(): CacheOptions | undefined {
		return (this.coreConfigurations && this.coreConfigurations.assetCacheOptions) || undefined;
	}

	// Leaving this as uncomputed on purpose
	// Hydration is not using an observable
	get assetRegisters(): IApplicationAssetRegister[] | undefined {
		const appHydration = getAppHydration(this.manifest, this.appParamsHelper);
		if (appHydration && appHydration.app && appHydration.app.assetRegister) {
			return appHydration.app.assetRegister;
		}
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get appComponentConfiguration(): IAppConfiguration | undefined {
		const appConfig = this.getComponentConfiguration('app');
		if (appConfig) {
			return appConfig;
		}
		return;
	}

	@computed
	get assets(): IAssets | undefined {
		const assets = this.configuration && this.configuration.assetOverrides;
		if (assets && utils.typeChecks.isNullOrEmpty(assets.baseUrl)) {
			assets.baseUrl = this.assetsBaseUrl;
		}
		return assets;
	}

	@computed
	get configuration(): IConfiguration | undefined {
		if (this.rawConfiguration) {
			return this.rawConfiguration as IConfiguration;
		}
	}
	@computed
	get styles(): IStyles | undefined {
		if (this.rawStyles) {
			if (this.rawTheme) {
				const { theme: stylesTheme = {}, ...styles } = this.rawStyles;
				const dirty = (obj: any) => JSON.parse(JSON.stringify(obj));
				const theme = utils.object.deepMergeObjectsWithOptions({ arrayMergeStrategy: 'AppendDeepCopy' }, this.rawTheme, dirty(stylesTheme));
				return { theme, ...styles } as IStyles;
			}
			return this.rawStyles as IStyles;
		}
	}
	@computed
	get libraryComponentsConfiguration(): any | undefined {
		return utils.object.deepMergeObjects(LIBRARY_COMPONENTS_CONFIGURATION, (this.configuration && this.configuration.libraryComponents) || {});
	}
	@computed
	get coreConfigurations(): ICoreConfigurations {
		let response: ICoreConfigurations | undefined;
		if (this.configuration && this.configuration.core) {
			response = this.configuration.core;
		}
		if (!response) {
			response = {};
		}
		return response;
	}
	@computed
	get theme(): any | undefined {
		return this.styles && this.styles.theme;
	}
	@computed
	get componentConfiguration(): IComponentConfigurations {
		if (this.configuration && this.configuration.components) {
			return this.configuration.components;
		}
		return {};
	}
	@computed
	get componentStyles(): IComponentStyles {
		if (this.rawStyles && this.styles && this.styles.components) {
			return this.styles.components;
		}
		return {};
	}

	@computed
	get isSnapshot(): boolean {
		const value = utils.url.getQueryStringValue({ key: 'isSnapshot' }, window.location.search);
		return value === 'true';
	}

	getInput(inputKey: keyof IInputs) {
		return this.appParamsHelper.inputs && this.appParamsHelper.inputs[inputKey];
	}
	getComponentConfiguration<K extends keyof IComponentConfigurations>(componentKey: K) {
		return this.componentConfiguration[componentKey];
	}
	getComponentStyle<K extends keyof IComponentStyles>(componentKey: K, ...childKeys: string[]): IComponentStyle | undefined {
		let response: IComponentStyle | undefined = this.componentStyles[componentKey] as IComponentStyle;
		if (childKeys) {
			for (const childKey of childKeys) {
				if (response && response.children && (response.children as any)[childKey]) {
					response = (response.children as any)[childKey];
				}
				else {
					response = undefined;
					break;
				}
			}
		}
		return response;
	}
	getDimensions = (): IPixelDimensions => {
		return utils.layout.getHtmlElementSize(this.htmlElement);
	}
	getCoreEmbed(embedId: string): IApplicationEmbedProps | undefined {
		if (this.configuration && this.configuration.core && this.configuration.core.embedding && this.configuration.core.embedding.embeds) {
			const embed = this.configuration.core.embedding.embeds.find(embed => embed.embedId === embedId);
			const hostname = this.configuration.core.embedding.hostname;
			if (embed) {
				const response: IApplicationEmbedProps = { ...embed };
				if (hostname && !response.hostname) {
					response.hostname = hostname;
				}
				return response;
			}
		}
	}
	orchestrateEmbed(rawEmbed: Partial<IApplicationEmbedProps>, defaultTemplateId: string, inputs?: IApplicationEmbedInput[], onLoad?: (element: HTMLElement) => void): IApplicationEmbedProps {
		const { appStore } = this.storeContext;
		const embedId = rawEmbed && rawEmbed.embedId;
		const coreEmbed: Partial<IApplicationEmbedProps> = (embedId && appStore.getCoreEmbed(embedId)) || {};
		const props: IApplicationEmbedProps = utils.object.deepMergeObjects(
			{
				templateId: defaultTemplateId,
				hostname: this.manifest.getBaseUrl(''),
			},
			coreEmbed,
			rawEmbed,
		);
		props.inputs = [
			...(props.inputs || []),
			...(inputs || []),
		];
		if (onLoad) {
			props.onLoad = onLoad;
		}
		return props;
	}

	@computed
	get globalFormatsByKey(): models.query.IGlobalFormatsByKey {
		const globalQueryFormats = (this.coreConfigurations && this.coreConfigurations.globalQueryFormats) || [];
		return utils.collection.pivotCollection(globalQueryFormats, item => item.key);
	}

	@computed
	get appCardProps(): ICardProps | undefined {
		const cardPropsRaw = this.appComponentConfiguration && this.appComponentConfiguration.cardProps;
		let cardProps: ICardProps | undefined = this.mergeQueriesAndProps(cardPropsRaw);
		if (cardProps && !cardProps.footerDisclaimers) {
			const globalDisclaimerStore = getGlobalDisclaimerStore();
			if (globalDisclaimerStore) {
				const appKey = getAppKey(this.manifest, this.appParamsHelper);
				const disclaimerOptions = this.coreConfigurations.disclaimerOptions;
				const level = (disclaimerOptions && disclaimerOptions.level) || models.disclaimers.GlobalDisclaimerLevel.app;
				const disclaimers = globalDisclaimerStore.get(level, appKey);
				if (disclaimers && disclaimers.length > 0) {
					const separator = (disclaimerOptions && disclaimerOptions.separator) || ' ';
					if (!cardProps) {
						cardProps = {};
					}
					cardProps.footerDisclaimers = {
						separator,
						disclaimers: disclaimers.map((disclaimer) => {
							return {
								text: disclaimer.text,
								hasHtmlContent: disclaimer.hasHtml,
								superscript: disclaimer.superscript.toString(),
							};
						}),
					};

				}
			}
		}
		return cardProps;
	}
}
